import CancellationError from '@/common/utils/errors/CancellationError';
import { formatArrayIn } from '@/common/utils/formatQueryParams';
import { isFunction, isEqual, merge as mergeObjects, castArray, difference, omit } from 'lodash-es';
import CommonError from '@/common/utils/errors/CommonError';
import waitTask from '@/common/utils/waitTask';
import createEntities from '@/common/store/modules/common/common.entities';
import cleanObjectProperties from '@/common/utils/cleanObjectProperties';
import { CommonState } from '@/common/store/modules/common/types/CommonState';
import { ActionsArgs } from '@/common/store/modules/common/types/ActionArgs';
import { DEFAULT_REQUEST_VIEW } from '@/common/store/modules/common/common.constants';

const FETCH_MARKER_LIMIT = 20;

interface CustomActionPayload {
  id: string,
  actionBody: FetchRequestBody,
  action: string,
  method: Method
}

interface ItemsData {
  data?: object[],
  count?: number
}

interface SetItemsPayload {
  data?: ItemsData,
  view: string,
  merge?: boolean,
}

interface FetchOptions {
  [key: string]: any
}

interface UpdateActionPayload {
  id: ID,
  body: FetchRequestBody,
  merge: boolean,
  options: FetchRequestParams
}

interface AbortAndRemovePayload {
  actionName: string,
  payload: FetchRequestParams
}

interface FetchItemsPayload {
  options?: FetchOptions,
  view?: string,
  includeId?: ID[],
  marker?: ID,
  limit?: number,
  [key: string]: any
}

export function defaultPlural(data): any[] {
  return data?.data || (data instanceof Array && data) || [];
}

function applyOptionsForSettings(options) {
  if (!options.actions) {
    return {};
  }

  if (typeof options.actions === 'function') {
    return options.actions(options);
  }

  return options.actions;
}

export function resourceFunctor(resource: Resource, store: CommonStore<unknown, unknown>) {
  if (typeof resource === 'function') {
    return resource(store);
  }
  return Promise.resolve(resource);
}

const pagingParamsGetters = {
  common: (requestParams, { marker, limit }) => ({
    limit,
    marker,
    with_count: true,
    ...requestParams,
  }),
  s3: (requestParams, { marker, limit }) => mergeObjects({}, requestParams, {
    params: {
      marker,
      count: limit
    }
  })
};

const nextMarkerGetters = {
  common: (response, items): ID => response.pages?.find(cursor => cursor.rel === 'next')?.marker || items[items.length - 1]?.id,
  s3: (response): ID => response.next_marker
};

const pageIsLastGetters = {
  common: (response, nextMarker, items, limit): boolean => !nextMarker || items.length < limit,
  s3: (response, nextMarker): boolean => !response.is_truncated || !nextMarker
};

const createActions = <ModelType>(
  {
    Model,
    resource,
    name,
    singular,
    socketUpdates,
    pagingSupport,
    plural,
    actions,
    headers,
    url,
    config = {},
    pagingMode = 'common'
  }: ActionsArgs<ModelType>): CommonActionTree<CommonState> => ({
    FETCH_ITEMS(store, payload: FetchItemsPayload = {}): Promise<FetchResponse> {
      const { commit, dispatch } = store;
      const { options, view = DEFAULT_REQUEST_VIEW, ...params } = payload;

      commit('ADD_REQUEST_VIEW', view);
      commit('LOADING', view);
      commit('SET_MARKER', { view });

      return resourceFunctor(resource, store)
      .then(resourceStr => this.$api.get(resourceStr, params, { headers, ...options }))
      .then((res) => {
        dispatch('SET_ITEMS', { data: res.data, view, merge: options?.merge ?? true });
        commit('SET_REQUEST_PARAMS', { requestParams: params, view });

        return res;
      })
      .catch((error) => {
        commit('SET_ERRORS', error);

        throw error;
      })
      .finally(() => {
        commit('LOADED', view);
      });
    },

    FETCH_ITEMS_WITH_MARKER(store, payload: FetchItemsPayload = {}): Promise<FetchResponse> {
      const { commit } = store;
      const {
        options,
        includeId,
        marker,
        limit = FETCH_MARKER_LIMIT,
        view = DEFAULT_REQUEST_VIEW,
        ...params
      } = payload;
      const requestParams = cleanObjectProperties(params);

      commit('ADD_REQUEST_VIEW', view);
      commit('LOADING', view);
      commit('SET_MARKER', { view, marker });

      const pagingParams = pagingParamsGetters[pagingMode](requestParams, { marker, limit });

      return resourceFunctor(resource, store)
      .then(resourceStr => this.$api.get(resourceStr, pagingParams, { headers, ...options }))
      .then((res) => {
        const response = res?.data;
        if (!response) {
          throw new CommonError('Data is undefined');
        }
        const items = isFunction(plural) ? plural(response) : defaultPlural(response);

        const entities = createEntities({
          Model,
          items,
        });

        if (marker) {
          commit('PUSH_ITEMS', { ...entities, view });
        } else {
          commit('SET_ITEMS', { ...entities, includeId, view });
        }

        const nextMarker = nextMarkerGetters[pagingMode](response, items);
        const isLastPage = pageIsLastGetters[pagingMode](response, nextMarker, items, limit);

        commit('SET_IS_LAST_PAGE', { isLastPage, view });
        commit('SET_MARKER', {
          marker: isLastPage ? undefined : nextMarker,
          view
        });

        return response;
      })
      .then((data) => {
        const { count } = data;
        commit('SET_REQUEST_PARAMS', { requestParams, view });
        commit('SET_COUNT', { count, view });

        return data;
      })
      .catch((error) => {
        if (error instanceof CancellationError) {
          throw error;
        }
        commit('SET_ITEMS', { ids: [], byId: {}, view });
        commit('SET_MARKER', { marker: undefined, view });
        commit('SET_IS_LAST_PAGE', { isLastPage: false, view });
        commit('SET_ERRORS', error);

        throw error;
      })
      .finally(() => {
        commit('LOADED', view);
      });
    },

    FETCH_ITEM_WITH_FILTER(store, payload: FetchItemsPayload = {}): Promise<FetchResponse> {
      const { commit, state, dispatch } = store;
      const {
        id,
        options,
        strictFiltersMatch = true,
        view = DEFAULT_REQUEST_VIEW
      } = payload;

      commit('ADD_REQUEST_VIEW', view);
      const params = omit(state.viewLists[view].requestParams, ['sort']);
      const ids = castArray(id);

      return resourceFunctor(resource, store)
      .then(resourceStr => this.$api.get(resourceStr, {
        // NOTE (mpetrov): restParams must rewrite ID field as well. It's important when filter by ID is applied
        id: formatArrayIn(ids),
        ...params,
      }, { headers, ...options }))
      .then((res) => {
        if (strictFiltersMatch && !isEqual(params, omit(state.viewLists[view].requestParams, ['sort']))) {
          // NOTE (mpetrov): VSTOR-78856 re-fetch item on filters change
          console.warn('Filter changed. Re-fetch data with update filter params');
          return dispatch('FETCH_ITEM_WITH_FILTER', {
            ...payload,
            strictFiltersMatch: false
          });
        }
        return res;
      })
      .then((res) => {
        const response = res?.data;
        const items = isFunction(plural) ? plural(response) : defaultPlural(response);
        const itemIds = items.map((item) => item.id);
        const itemsToRemove = difference(ids, itemIds);

        if (itemsToRemove.length) {
          commit('REMOVE_FROM_LIST', { id: itemsToRemove, view });
        }

        if (items.length) {
          commit('CHANGE_ITEMS', { items: items.map(item => new Model(item)), view });
        }

        return res;
      })
      .catch((error) => {
        commit('SET_ERRORS', error);
        throw error;
      });
    },

    FETCH_ITEM(store, { id, params = {}, merge = true, options = {}, view = DEFAULT_REQUEST_VIEW, addToList = true }: FetchItemsPayload): Promise<FetchResponse> {
      const { commit } = store;

      commit('ADD_REQUEST_VIEW', view);
      return resourceFunctor(resource, store)
      .then(resourceStr => this.$api.get(`${resourceStr}/${id}`, params, options))
      .then((res) => {
        const data = isFunction(singular) ? singular(res.data) : res.data;
        const item = new Model(data);
        commit('CHANGE', { item, view, addToList, merge });
        return res;
      })
      .catch((error) => {
        commit('SET_ERRORS', error);

        throw error;
      });
    },

    SET_ITEMS({ commit }, { data = {}, view = DEFAULT_REQUEST_VIEW, merge = true }: SetItemsPayload) {
      const items = isFunction(plural) ? plural(data) : defaultPlural(data);
      commit('SET_COUNT', { count: data.count, view });

      if (Array.isArray(items)) {
        commit('SET', { items: items.map(item => new Model(item)), view, merge });
      } else {
        throw new CommonError(`No items in fetch from ${resource}`);
      }

      return data;
    },

    DELETE(store, { item, payload }: { item: ModelType | ID, payload?: object }): Promise<FetchResponse> {
      const { commit } = store;
      const id = (<any>item).id || item;
      return resourceFunctor(resource, store)
      .then(resourceStr => this.$api.delete(`${resourceStr}/${id}`, payload))
      .then(res => config.needWaitTask ? waitTask(res) : res)
      .then((res) => {
        commit('CLEAR_ERRORS');
        !socketUpdates && commit('REMOVE', id); // If resource has socket updates it must be removed by socket notification
        return res;
      })
      .catch((error) => {
        commit('SET_ERRORS', error);

        throw error;
      });
    },

    DELETE_ITEMS({ commit, dispatch }, { items, payload }: { items: ModelType[], payload?: object }): Promise<any> {
      return Promise.all(items.map(item => dispatch('DELETE', { item, payload })))
      .catch((error) => {
        commit('SET_ERRORS', error);

        throw error;
      });
    },

    DELETE_ITEMS_STEP_BY_STEP({ commit, dispatch }, items: ModelType[]): Promise<any> {
      return items.reduce((promisesChain, item) => promisesChain.then(() => dispatch('DELETE', item)), Promise.resolve())
      .catch((error) => {
        commit('SET_ERRORS', error);
        throw error;
      });
    },

    ABORT_AND_REMOVE({ commit }, { actionName, payload = {} }: AbortAndRemovePayload): Promise<any> {
      return this.$abortEffect(actionName, payload)
      .then(() => {
        commit('REMOVE', payload.id);
      });
    },

    CREATE(store, item: ModelType): Promise<FetchResponse> {
      const { commit } = store;
      return resourceFunctor(resource, store)
      .then(resourceStr => this.$api.post(resourceStr, item, { headers }))
      .then((res) => {
        commit('CLEAR_ERRORS');
        if (res.data && !res.data.task_id) {
          if (pagingSupport) {
            const { sort, ...restParams } = store.state.viewLists[DEFAULT_REQUEST_VIEW]?.requestParams || {};
            if (Object.keys(restParams).length > 0) {
              store.dispatch('FETCH_ITEM_WITH_FILTER', { id: res.data.id });
              return res;
            }
          }

          const newItem = new Model(res.data);
          commit('CHANGE', { item: newItem, merge: false });
        }
        return res;
      })
      .catch((error) => {
        commit('SET_ERRORS', error);

        throw error;
      });
    },

    UPDATE(store, { id, body, merge = true, options = {} }: UpdateActionPayload): Promise<FetchResponse> {
      const { commit } = store;
      const method = options.method || config.updateMethod || 'patch';

      if (!this.$api[method]) {
        return Promise.reject(new CommonError(`No request method ${method} for endpoint ${resource}/${id}`));
      }

      return resourceFunctor(resource, store)
      .then(resourceStr => this.$api[method](`${resourceStr}/${id}`, body, options))
      .then((res) => {
        if (res.data && !res.data.task_id) {
          commit('CHANGE', { item: new Model(res.data), merge });
        }
        return res;
      })
      .catch((error) => {
        commit('SET_ERRORS', error);

        throw error;
      });
    },

    CHANGE({ commit }, data: ItemsData): void {
      commit('CHANGE', { item: new Model(data) });
    },

    UPDATE_ITEMS({ commit, dispatch }, items: ModelType[]): Promise<any> {
      return Promise.all(items.map(item => dispatch('UPDATE', item)))
      .catch((error) => {
        commit('SET_ERRORS', error);

        throw error;
      });
    },

    RUN_CUSTOM_ACTION(store, { id, actionBody, action = 'action', method = 'post' }: CustomActionPayload): Promise<FetchResponse> {
      const { commit } = store;

      commit('PROCESSING_CUSTOM_ACTION', id);
      return resourceFunctor(resource, store)
      .then(resourceStr => this.$api[method](`${resourceStr}/${id}/${action}`, actionBody))
      .then((res) => {
        commit('CLEAR_ERRORS');
        commit('FINISHED_CUSTOM_ACTION', id);
        return res;
      })
      .catch((error) => {
        commit('SET_ERRORS', error);
        commit('FINISHED_CUSTOM_ACTION', id);
        commit('errors/ADD_ERROR', error, { root: true });

        throw error;
      });
    },

    FETCH_VIEWS_BY_NOTIFICATION({ state, dispatch, commit }, payload: FetchItemsPayload): Promise<any> {
      // NOTE (mpetrov): in order to react to notifications correctly we need
      // 1. update all the item's properties
      // 2. update `list` arrays in every viewList according to active filters
      // 3. if an item do not match the filters, we must not display it in the list
      const activeViews = Object.keys(state.viewLists);
      return Promise.all([
        // fetch properties of the updated item without adding to the list
        dispatch('FETCH_ITEM', { ...payload, addToList: false }),
        // update all the lists in activeViews according to applied filters
        ...activeViews.map(view => dispatch('FETCH_ITEM_WITH_FILTER', { ...payload, view }))
      ]).catch((error) => {
        commit('SET_ERRORS', error);
        throw error;
      });
    },

    GET_OR_LOAD({ getters, dispatch }, { id }): Promise<any> {
      const item = getters.getItemById(id);
      if (item) {
        return Promise.resolve(item);
      }
      return dispatch('FETCH_ITEM', { id })
      .then(() => getters.getItemById(id));
    },

    ...applyOptionsForSettings({ Model, resource, name, singular, plural, actions, headers, url })
  });

export default createActions;
